import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { safeString } from './useAiChatAssistant';

const q_Chat = queryNoTracking('Workflow_QueryShaping_Chat', 0, true);

/**
 * The ONE definition of the Workflow_QueryShaping_Chat request envelope, shared by
 * every assistant client (the full AccentQueryShapingAssistantDlg and the chat-only
 * AccentAssistantChatPanel). Defaults describe a CHAT-ONLY turn — no artifacts, no
 * templates — and callers override just the fields their host actually uses, so a
 * server contract change only ever has one client-side home.
 */
export const buildAssistantChatRequest = (fields) => ({
    prompt: '',
    messages: [],
    queries: [],
    shapes: [],
    allowNewShapes: false,
    allowNewQueries: false,
    templateValues: {},
    queryAllowedTemplates: [],
    shapeAllowedTemplates: [],
    variablesJson: '{}',
    runtimeNotes: [],
    hostLabel: '',
    sessionOptions: {},
    promptIntent: '',
    ...fields,
});

/**
 * Sends one chat turn. Handled 500s return the exception envelope instead of
 * throwing; the guard throws its translated message so callers can report it as an
 * error turn rather than the envelope masquerading as a chat reply.
 */
export const sendAssistantChatTurn = async (fields) => (
    throwIfFrameworkException(await q_Chat.getFirstOrDefault(buildAssistantChatRequest(fields)))
);

/**
 * Normalizes the CHAT lane of a chat response (the server returns PascalCase, older
 * cached shapes camelCase). Artifact-heavy consumers (queries/shapes/checklist/review)
 * read those extra fields off the raw response themselves.
 */
export const parseAssistantChatResponse = (res) => ({
    message: safeString(res?.Message ?? res?.message),
    // The server's oracle round record: a hidden "tool" transcript message — never
    // rendered, but round-tripped so the next turn refines the previous brief.
    toolRecord: safeString(res?.ToolRecord ?? res?.toolRecord),
    // Optional second-audience evidence (the oracle's technical findings): the visible
    // message omits it; opt-in hosts render it behind a collapsed expander.
    technicalDetails: safeString(res?.TechnicalDetails ?? res?.technicalDetails),
    // Severity-typed server notices ('error' vs softer limitations/notes).
    notices: (res?.Notices ?? res?.notices ?? [])
        .map((n) => ({
            text: safeString(n?.Text ?? n?.text),
            severity: safeString(n?.Severity ?? n?.severity).toLowerCase(),
        }))
        .filter((n) => n.text),
    // Profile context contract violations (host wiring problems, not user errors).
    contextWarnings: (res?.ContextWarnings ?? res?.contextWarnings ?? []).map(safeString).filter(Boolean),
    // Pipeline usage counters for the turn (every AI model / data API call end-to-end).
    aiCallCount: Number(res?.AiCallCount ?? res?.aiCallCount ?? 0),
    dataApiCallCount: Number(res?.DataApiCallCount ?? res?.dataApiCallCount ?? 0),
});
